import { useMemo } from 'react';
import { type PropsValue } from 'react-select';

import { type PromoCodeOption } from 'hb-react/shared/components/ReactSelect/ReactSelectPromoCode/types';
import { getDiscountValues } from 'hb-react/shared/utils/PromoCode/promoCodeUtils';
import { type Currency } from 'hb-react/types/network';

interface UsePromoCodesDiscountParams {
  currency: Currency;
  multiplePromoCodesEnabled?: boolean;
  price: number;
  selectedPromoCodes: PropsValue<Pick<PromoCodeOption, 'valueOff'>>;
}

interface UsePromoCodesDiscountResult {
  discount: {
    newPrice: string | null;
    oldPrice: string | null;
    valueOff: number | null;
  };
}

export const usePromoCodesDiscount = ({
  selectedPromoCodes,
  price,
  currency,
  multiplePromoCodesEnabled,
}: UsePromoCodesDiscountParams): UsePromoCodesDiscountResult => {
  const discount = useMemo(() => {
    if (!price) {
      return {
        newPrice: null,
        oldPrice: null,
        valueOff: null,
      };
    }

    if (multiplePromoCodesEnabled) {
      // For multiple promo codes, calculate the highest discount
      const promoCodes = Array.isArray(selectedPromoCodes)
        ? selectedPromoCodes
        : [];

      if (promoCodes.length === 0) {
        return getDiscountValues({
          valueOff: null,
          price,
          currency,
        });
      }

      const discountValues = promoCodes.map(({ valueOff }) => valueOff || 0);
      const maxValueOff = Math.max(...discountValues);

      // If all discounts are 0, treat it as no discount
      const finalValueOff = maxValueOff > 0 ? maxValueOff : null;

      return getDiscountValues({
        valueOff: finalValueOff,
        price,
        currency,
      });
    }

    return getDiscountValues({
      valueOff:
        (selectedPromoCodes as Pick<PromoCodeOption, 'valueOff'>)?.valueOff ||
        0,
      price,
      currency,
    });
  }, [selectedPromoCodes, price, currency, multiplePromoCodesEnabled]);

  return {
    discount,
  };
};
